import { Lightbulb, Settings2, ArrowRight, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Estimate quality levels - honest framing about what the estimate provides
 */
export type EstimateQuality = 'starting-point' | 'refined' | 'customized' | 'validated';

// Legacy alias for backwards compatibility
export type ConfidenceLevel = EstimateQuality;

interface EstimateQualityIndicatorProps {
  quality: EstimateQuality;
  refinements: RefinementStep[];
  className?: string;
}

export interface RefinementStep {
  id: string;
  label: string;
  impact: string;
  action?: string; // What to click/do
  completed: boolean;
}

const QUALITY_CONFIG: Record<EstimateQuality, { 
  label: string; 
  description: string;
  color: string; 
  bgColor: string;
}> = {
  'starting-point': {
    label: 'Starting Point',
    description: 'Based on typical assumptions',
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
  },
  'refined': {
    label: 'Refined Estimate',
    description: 'Customized to your workload',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
  },
  'customized': {
    label: 'Tailored Estimate',
    description: 'Based on your specific usage patterns',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
  'validated': {
    label: 'Production-Grade',
    description: 'Ready for procurement discussions',
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
  },
};

/**
 * Shows estimate quality without misleading percentages.
 * Focuses on what refinements the user can make and their impact.
 */
export function EstimateQualityIndicator({
  quality,
  refinements,
  className,
}: EstimateQualityIndicatorProps) {
  const config = QUALITY_CONFIG[quality];
  const incompleteSteps = refinements.filter(r => !r.completed);

  return (
    <div className={cn('space-y-3', className)}>
      {/* Quality Badge - no percentage */}
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-full',
            config.bgColor
          )}
        >
          <Lightbulb className={cn('h-4 w-4', config.color)} />
          <span className={cn('text-sm font-medium', config.color)}>
            {config.label}
          </span>
        </div>
        <span className="text-sm text-muted-foreground">
          {config.description}
        </span>
      </div>

      {/* Actionable Refinements */}
      {incompleteSteps.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Settings2 className="h-4 w-4 shrink-0" />
            <span className="font-medium text-foreground">Refine your estimate:</span>
          </div>
          <div className="space-y-1.5 pl-6">
            {incompleteSteps.slice(0, 2).map((step) => (
              <div 
                key={step.id} 
                className="flex items-start gap-2 text-sm group"
              >
                <ChevronRight className="h-4 w-4 mt-0.5 shrink-0 text-primary/70" />
                <div>
                  <span className="font-medium text-foreground">{step.label}</span>
                  <span className="text-muted-foreground"> — {step.impact}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Legacy wrapper - maps old API to new component
 */
export function ConfidenceIndicator({
  level,
  percentage: _percentage, // Ignored - we don't show percentages anymore
  nextSteps,
  className,
}: {
  level: ConfidenceLevel;
  percentage: number;
  nextSteps: string[];
  className?: string;
}) {
  // Convert legacy nextSteps to new format
  const refinements: RefinementStep[] = nextSteps.map((step, i) => ({
    id: `legacy-${i}`,
    label: step,
    impact: 'Improves accuracy',
    completed: false,
  }));

  return (
    <EstimateQualityIndicator
      quality={level as EstimateQuality}
      refinements={refinements}
      className={className}
    />
  );
}

/**
 * Calculate estimate quality and actionable refinement steps
 */
export function calculateConfidence(params: {
  hasBasicInputs: boolean;
  hasCustomPersonas: boolean;
  hasModelSelected: boolean;
  hasPrecisionTuned: boolean;
}): { level: EstimateQuality; percentage: number; nextSteps: string[] } {
  const { hasBasicInputs, hasCustomPersonas, hasModelSelected, hasPrecisionTuned } = params;
  
  const nextSteps: string[] = [];

  // Build actionable next steps with impact explanations
  if (!hasModelSelected) {
    nextSteps.push('Select a model (affects memory & throughput)');
  }

  if (!hasCustomPersonas) {
    nextSteps.push('Define user mix (affects peak load calculation)');
  }

  if (!hasPrecisionTuned && (hasModelSelected || hasCustomPersonas)) {
    nextSteps.push('Tune precision (balances quality vs. efficiency)');
  }

  // Determine quality level based on what's been configured
  let level: EstimateQuality;
  let completedCount = 0;
  if (hasBasicInputs) completedCount++;
  if (hasModelSelected) completedCount++;
  if (hasCustomPersonas) completedCount++;
  if (hasPrecisionTuned) completedCount++;

  if (completedCount >= 4) {
    level = 'validated';
  } else if (completedCount >= 3) {
    level = 'customized';
  } else if (completedCount >= 2) {
    level = 'refined';
  } else {
    level = 'starting-point';
  }

  // Return percentage for backwards compatibility (but UI ignores it)
  const percentage = Math.min(100, completedCount * 25);

  return {
    level,
    percentage,
    nextSteps: nextSteps.slice(0, 2),
  };
}

/**
 * More detailed version for the full refinement panel
 */
export function calculateRefinementSteps(params: {
  hasBasicInputs: boolean;
  hasCustomPersonas: boolean;
  hasModelSelected: boolean;
  hasPrecisionTuned: boolean;
}): RefinementStep[] {
  const { hasBasicInputs, hasCustomPersonas, hasModelSelected, hasPrecisionTuned } = params;

  return [
    {
      id: 'basic-inputs',
      label: 'User count & use case',
      impact: 'Sets baseline concurrency requirements',
      action: 'Adjust in Quick Start above',
      completed: hasBasicInputs,
    },
    {
      id: 'model-selected',
      label: 'Select AI model',
      impact: 'Determines memory footprint & throughput capacity',
      action: 'Choose from sidebar Model dropdown',
      completed: hasModelSelected,
    },
    {
      id: 'custom-personas',
      label: 'Define user personas',
      impact: 'Refines peak load and session patterns',
      action: 'Add personas in User Types section',
      completed: hasCustomPersonas,
    },
    {
      id: 'precision-tuned',
      label: 'Set precision level',
      impact: 'Balances response quality vs. GPU efficiency',
      action: 'Adjust Precision slider in sidebar',
      completed: hasPrecisionTuned,
    },
  ];
}
